'use client';

import { useEffect } from 'react';

/**
 * Client-side freeze diagnostics for pages with a history of sporadic
 * main-thread lockups (company detail / network diagram).
 *
 * Three signals, all beaconed to /api/log/client-freeze → structured logger
 * → Loki/Grafana:
 *
 * 1. `longtask` — a PerformanceObserver entry whose duration crosses
 *    LONGTASK_REPORT_MS. Catches freezes the page RECOVERS from, with the
 *    breadcrumb trail showing what ran right before the block.
 * 2. `unclean-exit` — a localStorage watchdog. While mounted, a heartbeat
 *    persists {ts, url, breadcrumbs} twice a second; a clean unload
 *    (pagehide) deletes the record. If the main thread is frozen, pagehide
 *    never runs when the user kills the tab — so a stale leftover record on
 *    the NEXT visit is the black box of a hard freeze.
 * 3. `slow-op` — components report known-heavy operations (e.g. network
 *    graph layout) with their duration and input sizes via
 *    reportFreezeEvent(), giving a baseline even below freeze threshold.
 *
 * Breadcrumbs are module-global on purpose: the diagram (or any other
 * component) records them without prop-drilling, and whichever detector
 * fires attaches the same trail.
 */

type Breadcrumb = {
  label: string;
  data?: Record<string, unknown>;
  /** ms since navigation start (performance.now), rounded. */
  at: number;
};

const MAX_BREADCRUMBS = 25;
const breadcrumbs: Breadcrumb[] = [];

/** Extra context (e.g. euId) set by the mounted <FreezeReporter>. */
let pageContext: Record<string, unknown> = {};

/**
 * Set by the mounted <FreezeReporter>: persists the watchdog record NOW.
 * Called on every breadcrumb so a hard freeze can't eat the trail's tail —
 * the 500ms heartbeat alone loses whatever was pushed after its last tick.
 */
let persistWatchdogNow: (() => void) | null = null;

/** Record a breadcrumb. Cheap (array push + small localStorage write). */
export function freezeBreadcrumb(
  label: string,
  data?: Record<string, unknown>,
) {
  if (typeof window === 'undefined') return;
  breadcrumbs.push({ label, data, at: Math.round(performance.now()) });
  if (breadcrumbs.length > MAX_BREADCRUMBS) breadcrumbs.shift();
  try {
    persistWatchdogNow?.();
  } catch {
    // diagnostics must never break the page
  }
}

export type FreezeEventKind =
  | 'longtask'
  | 'unclean-exit'
  | 'slow-op'
  | 'elk-worker-broken'
  | 'elk-fallback';

/** Fire-and-forget beacon; never throws into the caller. */
export function reportFreezeEvent(
  kind: FreezeEventKind,
  detail: Record<string, unknown>,
) {
  if (typeof window === 'undefined') return;
  try {
    void fetch('/api/log/client-freeze', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        kind,
        url: window.location.href,
        context: pageContext,
        breadcrumbs: breadcrumbs.slice(-10),
        ...detail,
      }),
    }).catch(() => {});
  } catch {
    // Never let diagnostics break the page it's diagnosing.
  }
}

/** Long tasks are ubiquitous at 50ms; only a multi-second block is a freeze. */
const LONGTASK_REPORT_MS = 2_000;
const HEARTBEAT_MS = 500;
/** A watchdog record this stale belongs to a dead (or hard-frozen) tab. */
const WATCHDOG_STALE_MS = 30_000;
const WATCHDOG_PREFIX = 'freeze-watchdog:';

function tabId(): string {
  try {
    let id = sessionStorage.getItem('freeze-watchdog-tab');
    if (!id) {
      id = Math.random().toString(36).slice(2, 10);
      sessionStorage.setItem('freeze-watchdog-tab', id);
    }
    return id;
  } catch {
    return 'unknown';
  }
}

/** Report + clear watchdog records left behind by tabs that never unloaded cleanly. */
function reportLeftoverWatchdogs() {
  try {
    const now = Date.now();
    for (let i = localStorage.length - 1; i >= 0; i--) {
      const key = localStorage.key(i);
      if (!key?.startsWith(WATCHDOG_PREFIX)) continue;
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      try {
        const rec = JSON.parse(raw) as { ts: number };
        // Fresh records belong to other live tabs — leave them alone.
        if (now - rec.ts < WATCHDOG_STALE_MS) continue;
        localStorage.removeItem(key);
        reportFreezeEvent('unclean-exit', {
          staleForMs: now - rec.ts,
          lastHeartbeat: rec,
        });
      } catch {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // localStorage unavailable (private mode etc.) — skip silently.
  }
}

type FreezeReporterProps = {
  /** Attached to every beacon, e.g. { page: 'company-detail', euId }. */
  context: Record<string, unknown>;
};

/**
 * Mount once on a page under investigation. Renders nothing.
 */
export function FreezeReporter({ context }: FreezeReporterProps) {
  // Keep the module-level context current across client-side navigations
  // (the mount effect below runs once, but euId can change in place).
  useEffect(() => {
    pageContext = context;
  }, [context]);

  useEffect(() => {
    reportLeftoverWatchdogs();

    // ── 1. Recovered freezes: long main-thread blocks ──────────────────────
    // Prefer the Long Animation Frames API (Chrome 123+): unlike bare
    // `longtask` entries it attributes the block to concrete scripts
    // (source URL, function, char position) — it names the culprit instead
    // of just proving a freeze happened.
    let observer: PerformanceObserver | null = null;
    try {
      const supported = PerformanceObserver.supportedEntryTypes ?? [];
      if (supported.includes('long-animation-frame')) {
        observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration < LONGTASK_REPORT_MS) continue;
            const loaf = entry as PerformanceEntry & {
              blockingDuration?: number;
              scripts?: {
                duration: number;
                invoker?: string;
                sourceURL?: string;
                sourceFunctionName?: string;
                sourceCharPosition?: number;
              }[];
            };
            reportFreezeEvent('longtask', {
              durationMs: Math.round(entry.duration),
              startMs: Math.round(entry.startTime),
              op: {
                name: 'long-animation-frame',
                blockingMs: Math.round(loaf.blockingDuration ?? 0),
                scripts: (loaf.scripts ?? [])
                  .slice()
                  .sort((a, b) => b.duration - a.duration)
                  .slice(0, 3)
                  .map((s) => ({
                    durationMs: Math.round(s.duration),
                    invoker: s.invoker,
                    sourceURL: s.sourceURL,
                    fn: s.sourceFunctionName,
                    charPos: s.sourceCharPosition,
                  })),
              },
            });
          }
        });
        observer.observe({ type: 'long-animation-frame', buffered: true });
      } else if (supported.includes('longtask')) {
        observer = new PerformanceObserver((list) => {
          for (const entry of list.getEntries()) {
            if (entry.duration >= LONGTASK_REPORT_MS) {
              reportFreezeEvent('longtask', {
                durationMs: Math.round(entry.duration),
                startMs: Math.round(entry.startTime),
              });
            }
          }
        });
        observer.observe({ type: 'longtask', buffered: true });
      }
    } catch {
      // Neither API available (Safari/Firefox) — watchdog still works.
    }

    // ── 2. Hard freezes: heartbeat that a clean unload deletes ─────────────
    const key = `${WATCHDOG_PREFIX}${tabId()}`;
    const beat = () => {
      try {
        // Chrome-only heap sample: a dead tab whose last heartbeats show heap
        // climbing toward jsHeapSizeLimit died of OOM thrash, not a JS block.
        const mem = (
          performance as Performance & {
            memory?: { usedJSHeapSize: number; jsHeapSizeLimit: number };
          }
        ).memory;
        localStorage.setItem(
          key,
          JSON.stringify({
            ts: Date.now(),
            url: window.location.href,
            context: pageContext,
            heapMB: mem ? Math.round(mem.usedJSHeapSize / 1048576) : undefined,
            heapLimitMB: mem
              ? Math.round(mem.jsHeapSizeLimit / 1048576)
              : undefined,
            breadcrumbs: breadcrumbs.slice(-10),
          }),
        );
      } catch {
        // Quota / private mode — heartbeat is best-effort.
      }
    };
    beat();
    persistWatchdogNow = beat;
    const interval = setInterval(beat, HEARTBEAT_MS);
    const clear = () => {
      try {
        localStorage.removeItem(key);
      } catch {
        // ignore
      }
    };
    window.addEventListener('pagehide', clear);

    return () => {
      observer?.disconnect();
      clearInterval(interval);
      persistWatchdogNow = null;
      window.removeEventListener('pagehide', clear);
      clear();
      pageContext = {};
    };
  }, []);

  return null;
}
